import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./OpportunitiesDialog.css";
import Tooltip from "@mui/material/Tooltip";
import { NumericFormat } from "react-number-format";

// Înregistrează localizarea în limba română pentru DatePicker.
registerLocale("ro", ro);

// Structura inițială a erorilor de validare.
const initialErrors = {
  client: "",
  department: "",
  project_name: "",
  responsible_person: "",
  manual_action: "",
  client_approach_date: "",
  chance_materialization: "",
  estimated_offer_value: "",
  currency: "",
  exchange_rate: "",
};

const EditOpportunities = ({
  open,
  toggleAddPopup,
  onEditOpportunities,
  selectedRow,
}) => {
  // State-ul principal al formularului de editare.
  const [formData, setFormData] = useState({
    id: "",
    title_opportunity: "",
    code_opportunity: "",
    client: "",
    department: "",
    project_name: "",
    responsible_person: "",
    manual_action: "",
    estimated_offer_value: "",
    currency: "",
    exchange_rate: "",
    estimated_offer_value_system: "",
    issued_offer_value: "",
    client_approach_date: "",
    chance_materialization: "",
    comments: "",
    has_offer: false,
  });

  // Păstrează valoarea inițială a acțiunii pentru a detecta dacă utilizatorul a modificat-o.
  const [initialManualAction, setInitialManualAction] = useState("");

  // Listele necesare pentru dropdown-uri.
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);

  // Moneda de sistem folosită pentru calculul valorii estimate în moneda internă.
  const [systemCurrency, setSystemCurrency] = useState(null);

  // State pentru erori și pentru momentul în care formularul a fost trimis.
  const [errors, setErrors] = useState(initialErrors);
  const [submitted, setSubmitted] = useState(false);

  // Transformă lista de clienți în format compatibil cu react-select.
  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.name })),
    [clients]
  );

  // Transformă lista de departamente în format compatibil cu react-select.
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.name })),
    [departments]
  );

  // Transformă lista de angajați în format compatibil cu react-select.
  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.id,
        label: `${e.first_name} ${e.last_name}`,
      })),
    [employees]
  );

  // Transformă lista de monede în opțiuni pentru react-select.
  const currencyOptions = useMemo(
    () =>
      currencies.map((c) => ({
        value: c.id,
        label: c.abbreviation ? `${c.abbreviation} ` : c.name,
      })),
    [currencies]
  );

  // Găsește opțiunea corespunzătoare unei valori stocate în state.
  const findOption = (options, value) =>
    options.find((o) => String(o.value) === String(value)) || null;

  // Formatează valorile numerice în stil românesc.
  const formatNumberRO = (value) => {
    if (value === null || value === undefined || value === "") return "";

    const raw = String(value).trim();
    const match = raw.match(/-?[\d.,\s]+/);
    if (!match) return raw;

    const numericPart = match[0].trim();
    let normalized = numericPart.replace(/\s/g, "");

    if (normalized.includes(",") && normalized.includes(".")) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (normalized.includes(",")) {
      normalized = normalized.replace(",", ".");
    }

    const n = Number(normalized);
    if (Number.isNaN(n)) return raw;

    const formatted = new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    const suffix = raw.replace(match[0], "").trim();
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  // La deschiderea dialogului, încarcă toate datele necesare pentru select-uri și calcule.
  useEffect(() => {
    if (!open) return;

    axiosInstance
      .get("/clients/")
      .then((response) => {
        const activeClients = response.data.filter((c) => c.status === "Active");
        setClients(activeClients);
      })
      .catch((error) => console.error("Error fetching clients:", error));

    axiosInstance
      .get("/departments/")
      .then((response) => setDepartments(response.data))
      .catch((error) => console.error("Error fetching departments:", error));

    axiosInstance
      .get("/employees/")
      .then((response) => setEmployees(response.data))
      .catch((error) => console.error("Error fetching employees:", error));

    axiosInstance
      .get("/currencies/")
      .then((response) => setCurrencies(response.data))
      .catch((error) => console.error("Error fetching currencies:", error));

    axiosInstance
      .get("/exchange-rate/")
      .then((response) => setExchangeRates(response.data))
      .catch((error) => console.error("Error fetching exchange rates:", error));

    axiosInstance
      .get("/system-currency/")
      .then((response) => {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        setSystemCurrency(data || null);
      })
      .catch((error) => console.error("Error fetching system currency:", error));
  }, [open]);

  // Când se selectează o oportunitate pentru editare, formularul este populat cu datele ei.
  // Tot aici se salvează valoarea inițială a câmpului manual_action.
  useEffect(() => {
    if (!selectedRow || !open) return;

    const initialAction = selectedRow.current_action_text || "";

    setFormData({
      id: selectedRow.id || "",
      title_opportunity: selectedRow.title_opportunity || "",
      code_opportunity: selectedRow.code_opportunity || "",
      client: selectedRow.client || "",
      department: selectedRow.department || "",
      project_name: selectedRow.project_name || "",
      responsible_person: selectedRow.responsible_person || "",
      manual_action: initialAction,
      estimated_offer_value:
        selectedRow.estimated_offer_value !== null &&
        selectedRow.estimated_offer_value !== undefined
          ? String(selectedRow.estimated_offer_value)
          : "",
      currency: selectedRow.currency || "",
      exchange_rate: selectedRow.exchange_rate || "",
      estimated_offer_value_system:
        selectedRow.estimated_offer_value_system !== null &&
        selectedRow.estimated_offer_value_system !== undefined
          ? String(selectedRow.estimated_offer_value_system)
          : "",
      issued_offer_value: selectedRow.issued_offer_value || "",
      client_approach_date: selectedRow.client_approach_date || "",
      chance_materialization:
        selectedRow.chance_materialization !== null &&
        selectedRow.chance_materialization !== undefined
          ? String(selectedRow.chance_materialization)
          : "",
      comments: selectedRow.comments || "",
      has_offer: !!selectedRow.has_offer,
    });

    setInitialManualAction(initialAction);
    setErrors(initialErrors);
    setSubmitted(false);
  }, [selectedRow, open]);

  // Verifică dacă oportunitatea are deja ofertă emisă.
  // În acest caz, anumite câmpuri nu mai pot fi editate.
  const hasOffer = !!formData.has_offer;

  // Verifică dacă moneda selectată este chiar moneda de sistem.
  const selectedCurrencyIsSystem = useMemo(() => {
    if (!systemCurrency || !formData.currency) return false;
    return String(formData.currency) === String(systemCurrency.currency);
  }, [formData.currency, systemCurrency]);

  // Filtrează cursurile valutare pentru moneda selectată și le sortează descrescător.
  const filteredExchangeRates = useMemo(() => {
    if (!formData.currency || selectedCurrencyIsSystem) return [];

    return [...exchangeRates]
      .filter((rate) => String(rate.currency) === String(formData.currency))
      .sort((a, b) => {
        const dateCompare = new Date(b.date) - new Date(a.date);
        if (dateCompare !== 0) return dateCompare;
        return Number(b.id) - Number(a.id);
      });
  }, [exchangeRates, formData.currency, selectedCurrencyIsSystem]);

  // Transformă cursurile filtrate în opțiuni pentru react-select.
  const exchangeRateOptions = useMemo(
    () =>
      filteredExchangeRates.map((r) => ({
        value: r.id,
        label: String(r.value ?? ""),
        dateLabel: r.date || "",
        rateValue: r.value,
      })),
    [filteredExchangeRates]
  );

  // Dacă oportunitatea nu are ofertă emisă:
  // - setează automat ultimul curs disponibil
  // - sau golește câmpurile dacă moneda nu este selectată / este moneda sistemului
  useEffect(() => {
    if (hasOffer) return;

    setFormData((prev) => {
      if (!prev.currency) {
        return {
          ...prev,
          exchange_rate: "",
          estimated_offer_value_system: "",
        };
      }

      if (selectedCurrencyIsSystem) {
        return {
          ...prev,
          exchange_rate: "",
        };
      }

      const existsInFiltered = filteredExchangeRates.some(
        (rate) => String(rate.id) === String(prev.exchange_rate)
      );

      if (existsInFiltered) return prev;

      const latestRate = filteredExchangeRates[0];

      return {
        ...prev,
        exchange_rate: latestRate ? latestRate.id : "",
      };
    });
  }, [formData.currency, selectedCurrencyIsSystem, filteredExchangeRates, hasOffer]);

  // Recalculează automat valoarea în moneda de sistem atunci când se modifică:
  // - valoarea estimată
  // - cursul de schimb
  // - moneda selectată
  // Acest calcul se face doar dacă oportunitatea nu are deja ofertă emisă.
  useEffect(() => {
    if (hasOffer) return;

    const rawValue = formData.estimated_offer_value;

    if (rawValue === "" || rawValue === null || rawValue === undefined) {
      setFormData((prev) => ({
        ...prev,
        estimated_offer_value_system: "",
      }));
      return;
    }

    const numericValue = Number(rawValue);

    if (Number.isNaN(numericValue)) {
      setFormData((prev) => ({
        ...prev,
        estimated_offer_value_system: "",
      }));
      return;
    }

    // Dacă moneda selectată este aceeași cu moneda de sistem,
    // nu mai este nevoie de conversie.
    if (selectedCurrencyIsSystem) {
      setFormData((prev) => ({
        ...prev,
        estimated_offer_value_system: numericValue.toFixed(2),
      }));
      return;
    }

    // Găsește cursul selectat și calculează valoarea convertită.
    const selectedRate = filteredExchangeRates.find(
      (rate) => String(rate.id) === String(formData.exchange_rate)
    );

    if (!selectedRate) {
      setFormData((prev) => ({
        ...prev,
        estimated_offer_value_system: "",
      }));
      return;
    }

    const calculated = numericValue * Number(selectedRate.value);

    setFormData((prev) => ({
      ...prev,
      estimated_offer_value_system: Number.isNaN(calculated)
        ? ""
        : calculated.toFixed(2),
    }));
  }, [
    formData.estimated_offer_value,
    formData.exchange_rate,
    selectedCurrencyIsSystem,
    filteredExchangeRates,
    hasOffer,
  ]);

  // Handler pentru câmpurile react-select.
  // Actualizează valoarea selectată și resetează câmpurile dependente dacă este nevoie.
  const setSelectValue = (field, option) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: option ? option.value : "",
      };

      // Dacă se schimbă moneda, trebuie resetat cursul și valoarea calculată.
      if (field === "currency") {
        next.exchange_rate = "";
        next.estimated_offer_value_system = "";
      }

      // Dacă se schimbă cursul, resetăm valoarea calculată pentru a fi refăcută.
      if (field === "exchange_rate") {
        next.estimated_offer_value_system = "";
      }

      return next;
    });

    // Șterge eroarea asociată câmpului modificat.
    setErrors((prev) => ({
      ...prev,
      [field]: "",
      ...(field === "currency" ? { exchange_rate: "" } : {}),
    }));
  };

  // Handler generic pentru input-urile standard.
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Handler special pentru câmpul "Chances of Materialization".
  // Permite introducerea de la tastatură și validează intervalul 0 - 100.
  const handleChanceMaterializationChange = (e) => {
    const { value } = e.target;

    setFormData((prev) => ({
      ...prev,
      chance_materialization: value,
    }));

    let errorMessage = "";

    if (value !== "") {
      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        errorMessage = "Value must be a number";
      } else if (numericValue < 0 || numericValue > 100) {
        errorMessage = "Value must be between 0 and 100";
      }
    }

    setErrors((prev) => ({
      ...prev,
      chance_materialization: errorMessage,
    }));
  };

  // Handler pentru DatePicker.
  // Transformă data selectată în format YYYY-MM-DD pentru backend.
  const handleDateChange = (date, fieldName) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [fieldName]: "" }));
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const isoDate = `${year}-${month}-${day}`;

    setFormData((prev) => ({ ...prev, [fieldName]: isoDate }));
    setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };

  // Validează datele formularului.
  // Verifică atât câmpurile obligatorii, cât și relațiile dintre valoare, monedă și curs.
  const validate = () => {
    const newErrors = {};

    if (!formData.client) newErrors.client = "Required";
    if (!formData.department) newErrors.department = "Required";
    if (!formData.project_name?.trim()) newErrors.project_name = "Required";
    if (!formData.responsible_person) newErrors.responsible_person = "Required";
    if (!formData.manual_action?.trim()) newErrors.manual_action = "Required";
    if (!formData.client_approach_date) newErrors.client_approach_date = "Required";

    if (
      formData.chance_materialization === "" ||
      formData.chance_materialization === null ||
      formData.chance_materialization === undefined
    ) {
      newErrors.chance_materialization = "Required";
    } else {
      const numericChance = Number(formData.chance_materialization);

      if (Number.isNaN(numericChance)) {
        newErrors.chance_materialization = "Value must be a number";
      } else if (numericChance < 0 || numericChance > 100) {
        newErrors.chance_materialization = "Value must be between 0 and 100";
      }
    }

    // Validările pentru valorile financiare se aplică doar dacă nu există ofertă emisă.
    if (!hasOffer) {
      const hasEstimatedValue =
        formData.estimated_offer_value !== "" &&
        formData.estimated_offer_value !== null;

      const hasCurrency = !!formData.currency;

      if (hasEstimatedValue && !hasCurrency) {
        newErrors.currency = "Required";
      }

      if (!hasEstimatedValue && hasCurrency) {
        newErrors.estimated_offer_value = "Required";
      }

      if (
        hasEstimatedValue &&
        hasCurrency &&
        !selectedCurrencyIsSystem &&
        !formData.exchange_rate &&
        filteredExchangeRates.length === 0
      ) {
        newErrors.exchange_rate = "No exchange rate available for selected currency";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Trimite formularul către componenta părinte pentru salvare.
  // Elimină din payload câmpurile care nu trebuie trimise sau nu au fost modificate.
  const handleSubmit = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload = { ...formData };

    const currentValue = (formData.manual_action || "").trim();
    const initialValue = (initialManualAction || "").trim();

    // Dacă acțiunea manuală nu a fost modificată, nu o trimitem în payload.
    if (currentValue === initialValue) {
      delete payload.manual_action;
    }

    // Normalizăm șansa de materializare ca număr.
    payload.chance_materialization =
      payload.chance_materialization === ""
        ? null
        : Number(payload.chance_materialization);

    // Dacă nu există ofertă, normalizăm valorile financiare.
    if (!hasOffer) {
      payload.estimated_offer_value =
        payload.estimated_offer_value === "" ? null : payload.estimated_offer_value;
      payload.currency = payload.currency || null;
      payload.exchange_rate = selectedCurrencyIsSystem
        ? null
        : payload.exchange_rate || null;
    } else {
      // Dacă există ofertă, eliminăm câmpurile care nu mai sunt editabile.
      delete payload.estimated_offer_value;
      delete payload.currency;
      delete payload.exchange_rate;
      delete payload.estimated_offer_value_system;
      delete payload.issued_offer_value;
    }

    // Câmp intern folosit doar în UI, nu trebuie trimis către backend.
    delete payload.has_offer;

    onEditOpportunities(payload);
  };

  // Variabile booleene pentru afișarea erorilor doar după submit.
  const showClientError = submitted && !!errors.client;
  const showDepartmentError = submitted && !!errors.department;
  const showProjectNameError = submitted && !!errors.project_name;
  const showResponsibleError = submitted && !!errors.responsible_person;
  const showActionError = submitted && !!errors.manual_action;
  const showEstimatedOfferValueError = submitted && !!errors.estimated_offer_value;
  const showCurrencyError = submitted && !!errors.currency;
  const showExchangeRateError = submitted && !!errors.exchange_rate;
  const showClientApproachError = submitted && !!errors.client_approach_date;
  const showChanceError = submitted && !!errors.chance_materialization;

  // Dacă șansa de materializare este 100%, câmpul devine blocat.
  const isChanceLocked = Number(formData.chance_materialization) === 100;

  return (
    <Dialog
      open={open}
      onClose={toggleAddPopup}
      maxWidth="md"
      fullWidth
      className="opportunities-dialog"
    >
      <DialogTitle className="opp-title">
        <span className="opp-header-title">Edit Opportunity</span>
      </DialogTitle>

      <DialogContent dividers className="opp-content">
        <div className="opp-grid">
          <div className="opp-form-group opp-full-width">
            <label className="opp-label">Opportunity Title</label>
            <input
              type="text"
              name="title_opportunity"
              value={formData.title_opportunity}
              onChange={handleChange}
              className="opp-input"
              placeholder="Enter opportunity title"
            />
          </div>

          <div className="opp-form-group">
            <label className="opp-label">
              Client <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              options={clientOptions}
              placeholder="Select Client"
              value={findOption(clientOptions, formData.client)}
              onChange={(opt) => setSelectValue("client", opt)}
              isClearable
              isSearchable
              classNamePrefix="opp-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                menu: (base) => ({ ...base, zIndex: 20000 }),
              }}
              className={showClientError ? "opp-select-error" : ""}
            />
            {showClientError && (
              <div className="opp-field-error">{errors.client}</div>
            )}
          </div>

          <div className="opp-form-group">
            <label className="opp-label">
              Department <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              options={departmentOptions}
              placeholder="Select Department"
              value={findOption(departmentOptions, formData.department)}
              onChange={(opt) => setSelectValue("department", opt)}
              isClearable
              isSearchable
              classNamePrefix="opp-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                menu: (base) => ({ ...base, zIndex: 20000 }),
              }}
              className={showDepartmentError ? "opp-select-error" : ""}
            />
            {showDepartmentError && (
              <div className="opp-field-error">{errors.department}</div>
            )}
          </div>

          <div className="opp-form-group">
            <label className="opp-label">
              Project Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="project_name"
              value={formData.project_name}
              onChange={handleChange}
              className={`opp-input ${showProjectNameError ? "opp-input-error" : ""}`}
              placeholder="Enter project name"
            />
            {showProjectNameError && (
              <div className="opp-field-error">{errors.project_name}</div>
            )}
          </div>

          <div className="opp-form-group">
            <label className="opp-label">
              Responsible Person <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              options={employeeOptions}
              placeholder="Select Responsible Person"
              value={findOption(employeeOptions, formData.responsible_person)}
              onChange={(opt) => setSelectValue("responsible_person", opt)}
              isClearable
              isSearchable
              classNamePrefix="opp-react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                menu: (base) => ({ ...base, zIndex: 20000 }),
              }}
              className={showResponsibleError ? "opp-select-error" : ""}
            />
            {showResponsibleError && (
              <div className="opp-field-error">{errors.responsible_person}</div>
            )}
          </div>

          <div className="opp-form-group opp-full-width">
            <label className="opp-label">
              Action <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              name="manual_action"
              value={formData.manual_action}
              onChange={handleChange}
              className={`opp-input ${showActionError ? "opp-input-error" : ""}`}
              placeholder="Enter action"
            />
            {showActionError && (
              <div className="opp-field-error">{errors.manual_action}</div>
            )}
          </div>

          {hasOffer ? (
            <div className="opp-full-width">
              <div className="opp-form-group">
                <label className="opp-label">Issued Offer Value</label>
                <input
                  type="text"
                  value={
                    formData.issued_offer_value
                      ? formatNumberRO(formData.issued_offer_value)
                      : ""
                  }
                  readOnly
                  className="opp-input opp-readonly-input"
                  placeholder="Issued offer value"
                />
              </div>
            </div>
          ) : (
            <div className="opp-full-width">
              <div className="opp-estimation-row">
                <div className="opp-estimation-col">
                  <label className="opp-label">Estimated Offer Value</label>
                  <NumericFormat
                    name="estimated_offer_value"
                    value={formData.estimated_offer_value}
                    onValueChange={({ value }) => {
                      handleChange({
                        target: { name: "estimated_offer_value", value: value || "" },
                      });
                    }}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    inputMode="decimal"
                    className={`opp-input ${showEstimatedOfferValueError ? "opp-input-error" : ""}`}
                    placeholder="Enter estimated value"
                  />
                  {showEstimatedOfferValueError && (
                    <div className="opp-field-error">{errors.estimated_offer_value}</div>
                  )}
                </div>

                <div className="opp-estimation-col">
                  <label className="opp-label">Currency</label>
                  <Select
                    options={currencyOptions}
                    placeholder="Select Currency"
                    value={findOption(currencyOptions, formData.currency)}
                    onChange={(opt) => setSelectValue("currency", opt)}
                    isClearable
                    isSearchable
                    classNamePrefix="opp-react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                      menu: (base) => ({ ...base, zIndex: 20000 }),
                    }}
                    className={showCurrencyError ? "opp-select-error" : ""}
                  />
                  {showCurrencyError && (
                    <div className="opp-field-error">{errors.currency}</div>
                  )}
                </div>

                <div className="opp-estimation-col">
                  <label className="opp-label">Exchange Rate</label>
                  {selectedCurrencyIsSystem ? (
                    <input
                      type="text"
                      value="Not needed"
                      readOnly
                      className="opp-input opp-readonly-input"
                    />
                  ) : (
                    <>
                      <Select
                        options={exchangeRateOptions}
                        placeholder={
                          formData.currency
                            ? "Select Exchange Rate"
                            : "Select Currency first"
                        }
                        value={findOption(exchangeRateOptions, formData.exchange_rate)}
                        onChange={(opt) => setSelectValue("exchange_rate", opt)}
                        isClearable
                        isSearchable
                        isDisabled={!formData.currency || exchangeRateOptions.length === 0}
                        classNamePrefix="opp-react-select"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        formatOptionLabel={(option, { context }) =>
                          context === "value"
                            ? option.label
                            : `${option.label}${option.dateLabel ? ` (${option.dateLabel})` : ""}`
                        }
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 20000 }),
                          menu: (base) => ({ ...base, zIndex: 20000 }),
                        }}
                        className={showExchangeRateError ? "opp-select-error" : ""}
                      />
                      {showExchangeRateError && (
                        <div className="opp-field-error">{errors.exchange_rate}</div>
                      )}
                    </>
                  )}
                </div>

                <div className="opp-estimation-col">
                  <label className="opp-label">
                    Estimated Value System{" "}
                    {systemCurrency?.currency_label
                      ? `(${systemCurrency.currency_label})`
                      : ""}
                  </label>
                  <NumericFormat
                    value={formData.estimated_offer_value_system || ""}
                    readOnly
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    className="opp-input opp-readonly-input"
                    placeholder="Calculated automatically"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="opp-form-group">
            <label className="opp-label">
              Initial Client Approach Date <span style={{ color: "red" }}>*</span>
            </label>
            <div
              className={`opp-datepicker-wrapper ${
                showClientApproachError ? "opp-input-error" : ""
              }`}
              style={{ position: "relative" }}
            >
              <DatePicker
                selected={
                  formData.client_approach_date
                    ? new Date(formData.client_approach_date)
                    : null
                }
                onChange={(date) => handleDateChange(date, "client_approach_date")}
                dateFormat="dd.MM.yyyy"
                locale="ro"
                placeholderText="dd.mm.yyyy"
                className="opp-datepicker-input"
                popperContainer={({ children }) => (
                  <div style={{ position: "fixed", zIndex: 9999 }}>
                    {children}
                  </div>
                )}
              />
              <span
                className="calendar-icon"
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
            </div>
            {showClientApproachError && (
              <div className="opp-field-error">{errors.client_approach_date}</div>
            )}
          </div>

          <div className="opp-form-group">
            <label className="opp-label">
              Chances of Materialization (%) <span style={{ color: "red" }}>*</span>
            </label>

            <Tooltip
              title={
                isChanceLocked
                  ? "This field is locked because the opportunity already has a contract."
                  : ""
              }
              arrow
              disableHoverListener={!isChanceLocked}
              placement="top"
            >
              <span style={{ display: "block" }}>
                <input
                  type="number"
                  name="chance_materialization"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.chance_materialization}
                  onChange={handleChanceMaterializationChange}
                  disabled={isChanceLocked}
                  className={`opp-input ${showChanceError ? "opp-input-error" : ""}`}
                  placeholder="Enter a value between 0 and 100"
                />
              </span>
            </Tooltip>

            {showChanceError && (
              <div className="opp-field-error">{errors.chance_materialization}</div>
            )}
          </div>

          <div className="opp-form-group opp-full-width">
            <label className="opp-label">Comments</label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows={4}
              className="opp-input opp-textarea"
              placeholder="Enter comments"
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions className="opp-actions">
        <Button onClick={toggleAddPopup} className="opp-btn opp-btn-cancel">
          Close
        </Button>

        <Button
          onClick={handleSubmit}
          variant="contained"
          className="opp-btn opp-btn-create"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOpportunities;